<template>
  <div class="phone-field" :data-testid="dataTestid ? `${dataTestid}-root` : undefined">
    <v-text-field
      ref="phone"
      :value="displayValue"
      :label="label"
      type="tel"
      autocomplete="tel"
      :outlined="outlined"
      :dense="dense"
      :disabled="disabled"
      hide-details="auto"
      :hint="hintMessage"
      :messages="standardMessages"
      :rules="fieldRules"
      :error="isAmbiguous"
      :error-messages="errorMessages"
      validate-on-blur
      class="phone-field__number"
      :data-testid="dataTestid || 'phone-field-input'"
      @input="onInput"
      @blur="onBlur"
    >
      <template #prepend-inner>
        <v-menu
          v-model="countryMenu"
          offset-y
          :close-on-content-click="false"
          max-height="320"
          min-width="280"
        >
          <template #activator="{ attrs }">
            <button
              v-bind="attrs"
              type="button"
              class="phone-field__country-button"
              :disabled="disabled"
              :aria-label="countryAriaLabel"
              :data-testid="dataTestid ? `${dataTestid}-country` : 'phone-field-country'"
              @mousedown.stop
              @mouseup.stop
              @click.stop.prevent="countryMenu = !countryMenu"
            >
              <span aria-hidden="true">{{ countryButtonFlag }}</span>
              <!-- Скрывается через visibility, а не v-if: ширина кнопки не меняется, иначе Vuetify
                   не пересчитывает отступ подписи outlined-поля и она съезжает влево. -->
              <span
                v-if="selectedCountryOption"
                :class="{ 'phone-field__calling-code--hidden': displayHasCallingCode }"
                :aria-hidden="displayHasCallingCode ? 'true' : undefined"
                data-testid="phone-field-calling-code"
              >+{{ selectedCountryOption.callingCode }}</span>
              <span class="phone-field__country-chevron" aria-hidden="true">▾</span>
            </button>
          </template>

          <v-card class="phone-field__country-menu">
            <div class="phone-field__country-search">
              <v-text-field
                ref="countrySearch"
                v-model="countrySearch"
                :placeholder="$t('phone_field.choose_country')"
                prepend-inner-icon="mdi-magnify"
                dense
                flat
                solo
                clearable
                hide-details
                data-testid="phone-field-country-search"
                @keydown.esc="countryMenu = false"
              />
            </div>
            <v-list dense class="phone-field__country-list">
              <v-list-item
                v-for="item in filteredFrequentCountries"
                :key="`frequent-${item.code}`"
                :data-country-code="item.code"
                @click="selectCountry(item.code)"
              >
                <v-list-item-title>
                  {{ item.flag }} {{ item.name }} · +{{ item.callingCode }}
                </v-list-item-title>
              </v-list-item>

              <v-divider v-if="showCountryDivider" />

              <v-list-item
                v-for="item in filteredOtherCountries"
                :key="item.code"
                :data-country-code="item.code"
                @click="selectCountry(item.code)"
              >
                <v-list-item-title>
                  {{ item.flag }} {{ item.name }} · +{{ item.callingCode }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
      <template v-if="showNeedsCheck" #append>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              color="warning"
              class="phone-field__needs-check"
              role="img"
              :aria-label="$t('phone_field.needs_check')"
              data-testid="phone-field-needs-check"
              v-on="on"
            >
              mdi-alert-outline
            </v-icon>
          </template>
          <span>{{ $t("phone_field.needs_check") }}</span>
        </v-tooltip>
      </template>
      <template #message="{ message }">
        <div v-if="candidates.length" class="phone-field__candidate-message">
          <span>{{ $t("phone_field.ambiguous") }}</span>
          <template v-for="(candidate, index) in candidates">
            <span v-if="index" :key="`separator-${candidate.e164}`"> · </span>
            <v-btn
              :key="candidate.e164"
              x-small
              text
              color="primary"
              class="phone-field__candidate"
              :disabled="disabled"
              data-testid="phone-field-candidate"
              @mousedown.stop
              @mouseup.stop
              @click.stop="selectCandidate(candidate)"
            >
              {{ countryFlag(candidate.country) }} {{ formatCandidate(candidate.e164) }}
            </v-btn>
          </template>
        </div>
        <span v-else>{{ message }}</span>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/max";
import { parsePhone } from "@/lib/phone/parse";
import { formatPhone } from "@/lib/phone/display";
import { resolvePhoneCountry } from "@/lib/phone/defaultCountry";

export const FREQUENT_COUNTRIES = [
  "RU", "KZ", "BY", "UZ", "GE", "AM", "KG", "TH", "AE",
  "TR", "RS", "ME", "PT", "CY", "ID", "VN", "DO",
];

function flagForCountry(country) {
  if (!country || country.length !== 2) return "🌐";
  return String.fromCodePoint(
    ...country.toUpperCase().split("").map((letter) => 127397 + letter.charCodeAt(0))
  );
}

// Список стран зависит только от языка: строится один раз на язык и делится всеми полями.
// Раньше каждый экземпляр строил свои ~245 записей (Intl.DisplayNames + сортировка) — на форме
// с несколькими телефонами и в unit-сьюте с сотнями смонтированных форм это была лишняя память.
const countryOptionsByLocale = new Map();

function buildCountryOptions(locale) {
  let names;
  try {
    names = new Intl.DisplayNames([locale], { type: "region" });
  } catch (_) {
    names = null;
  }

  const options = getCountries().map((code) => Object.freeze({
    code,
    name: names?.of(code) || code,
    callingCode: getCountryCallingCode(code),
    flag: flagForCountry(code),
  }));
  const byCode = new Map(options.map((option) => [option.code, option]));
  const frequent = FREQUENT_COUNTRIES.map((code) => byCode.get(code)).filter(Boolean);
  const frequentSet = new Set(FREQUENT_COUNTRIES);
  const rest = options
    .filter((option) => !frequentSet.has(option.code))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return Object.freeze([...frequent, ...rest]);
}

export function countryOptionsFor(locale) {
  if (!countryOptionsByLocale.has(locale)) {
    countryOptionsByLocale.set(locale, buildCountryOptions(locale));
  }
  return countryOptionsByLocale.get(locale);
}

export default {
  name: "PhoneField",
  props: {
    value: { type: [String, Number], default: "" },
    defaultCountry: { type: String, default: "" },
    mode: {
      type: String,
      default: "strict",
      validator: (value) => ["strict", "soft"].includes(value),
    },
    needsCheck: { type: Boolean, default: false },
    label: { type: String, default: "" },
    outlined: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
    hideDetails: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    dataTestid: { type: String, default: "" },
  },
  data() {
    return {
      selectedCountry: resolvePhoneCountry(this.defaultCountry),
      displayValue: "",
      rawValue: "",
      result: { status: "empty" },
      candidates: [],
      dirty: false,
      countryLocked: false,
      hasBlurred: false,
      infoKey: "",
      lastEmitted: undefined,
      countryMenu: false,
      countrySearch: "",
    };
  },
  computed: {
    locale() {
      return this.$i18n?.locale || "en";
    },
    countryOptions() {
      return countryOptionsFor(this.locale);
    },
    selectedCountryOption() {
      return this.countryOptions.find((option) => option.code === this.selectedCountry) || null;
    },
    // Код страны показывается один раз: номер в поле уже с «+код» — на кнопке только флаг.
    displayHasCallingCode() {
      return /^\s*(?:\+|00)/.test(this.displayValue);
    },
    countryButtonFlag() {
      return this.selectedCountryOption?.flag || "🌐";
    },
    countryAriaLabel() {
      if (!this.selectedCountryOption) return this.$t("phone_field.choose_country");
      return this.$t("phone_field.country_label", { country: this.selectedCountryOption.name });
    },
    filteredFrequentCountries() {
      return this.countryOptions
        .slice(0, FREQUENT_COUNTRIES.length)
        .filter(this.matchesCountrySearch);
    },
    filteredOtherCountries() {
      return this.countryOptions
        .slice(FREQUENT_COUNTRIES.length)
        .filter(this.matchesCountrySearch);
    },
    showCountryDivider() {
      return this.filteredFrequentCountries.length > 0 && this.filteredOtherCountries.length > 0;
    },
    showNeedsCheck() {
      return this.needsCheck && !this.dirty;
    },
    hintMessage() {
      if (this.hideDetails) return "";
      if (this.result.status !== "ok") return "";

      const country = this.nameForCountry(this.result.country);
      return [
        formatPhone(this.result.e164),
        country,
        this.infoKey ? this.$t(this.infoKey) : "",
      ].filter(Boolean).join(" · ");
    },
    countryName() {
      return this.nameForCountry(this.selectedCountry);
    },
    ruleMessage() {
      if (this.showNeedsCheck || this.result.status === "empty" || this.result.status === "ok") {
        return "";
      }
      if (this.result.status === "ambiguous") return this.$t("phone_field.ambiguous");

      const country = this.countryName || this.$t("phone_field.country_not_selected");
      if (this.result.reason === "too_short") return this.$t("phone_field.too_short", { country });
      if (this.result.reason === "too_long") return this.$t("phone_field.too_long", { country });
      return this.$t("phone_field.invalid", { country });
    },
    validationMessage() {
      return this.hasBlurred ? this.ruleMessage : "";
    },
    isAmbiguous() {
      return this.hasBlurred && this.result.status === "ambiguous";
    },
    errorMessages() {
      if (this.isAmbiguous) return [this.$t("phone_field.ambiguous")];
      return this.mode === "strict" && this.validationMessage ? [this.validationMessage] : [];
    },
    standardMessages() {
      // «Проверьте номер» — иконкой с подсказкой в поле (slot append), не строкой под ним.
      return this.mode === "soft" && this.validationMessage ? [this.validationMessage] : [];
    },
    fieldRules() {
      return [() => (this.mode === "soft" || !this.ruleMessage ? true : this.ruleMessage)];
    },
    isValid() {
      return this.mode === "soft" || !this.ruleMessage;
    },
  },
  watch: {
    value(value) {
      if (value === this.lastEmitted) {
        this.lastEmitted = undefined;
        return;
      }
      this.syncIncoming(value);
    },
    defaultCountry(value) {
      if (this.countryLocked) return;

      this.selectedCountry = resolvePhoneCountry(value);
      if (this.showNeedsCheck || !this.rawValue.trim()) return;

      this.infoKey = "";
      this.displayValue = this.formattedInput(this.rawValue);
      this.result = this.parseInput(this.displayValue);
      this.candidates = this.hasBlurred && this.result.status === "ambiguous"
        ? this.result.candidates
        : [];
      this.emitResult(this.result, this.rawValue);
      this.$nextTick(() => this.$refs.phone?.validate());
    },
    needsCheck() {
      this.syncIncoming(this.value);
    },
    // Меню открылось — фокус в поиск, чтобы сразу набирать название страны.
    countryMenu(open) {
      if (!open) return;
      setTimeout(() => {
        const search = this.$refs.countrySearch;
        if (search && typeof search.focus === "function") search.focus();
      }, 0);
    },
  },
  created() {
    this.syncIncoming(this.value);
  },
  methods: {
    nameForCountry(country) {
      if (!country) return "";
      try {
        return new Intl.DisplayNames([this.locale], { type: "region" }).of(country) || country;
      } catch (_) {
        return country;
      }
    },
    countryFlag(country) {
      return flagForCountry(country);
    },
    // Местный вид номера без кода и без национального префикса — код страны показан на кнопке
    // (как separateDialCode в intl-tel-input): «+351 925 129 909» → «925 129 909».
    nationalDisplay(e164) {
      const phone = parsePhoneNumberFromString(String(e164 || ""));
      if (!phone || phone.country !== this.selectedCountry) return formatPhone(e164);
      return formatPhone(e164).replace(/^\+\d+\s*/, "");
    },
    // Страна известна явно (выбрана, определена по «+» или пришла с сохранённым номером) —
    // цифры без «+» читаются как местный номер этой страны; два прочтения остаются только для
    // страны по умолчанию, когда номер могли вставить с кодом без «+» (REQ-01).
    parseInput(text) {
      const parsed = parsePhone(text, this.selectedCountry);
      if (!this.countryLocked || parsed.status !== "ambiguous") return parsed;

      const [local] = parsed.candidates;
      return { status: "ok", e164: local.e164, country: local.country, via: "local" };
    },
    formatCandidate(value) {
      return formatPhone(value);
    },
    matchesCountrySearch(option) {
      const query = String(this.countrySearch || "").trim().toLocaleLowerCase(this.locale);
      if (!query) return true;

      const callingCodeQuery = query.replace(/^\+/, "");
      return (
        option.name.toLocaleLowerCase(this.locale).includes(query) ||
        option.code.toLocaleLowerCase(this.locale).includes(query) ||
        option.callingCode.includes(callingCodeQuery)
      );
    },
    selectCountry(country) {
      this.onCountryChange(country);
      this.countryMenu = false;
      this.countrySearch = "";
    },
    syncIncoming(value) {
      const text = String(value ?? "");
      this.dirty = false;
      this.hasBlurred = false;
      this.infoKey = "";
      this.candidates = [];
      this.rawValue = text;
      this.countryLocked = /^\s*(?:\+|00)/.test(text);

      if (this.needsCheck) {
        this.displayValue = text;
        this.result = text.trim() ? { status: "invalid", reason: "not_a_number" } : { status: "empty" };
        return;
      }

      const parsed = parsePhone(text, this.selectedCountry);
      this.result = parsed;
      if (parsed.status === "ok") {
        if (parsed.country) this.selectedCountry = parsed.country;
        this.displayValue = this.nationalDisplay(parsed.e164);
      } else {
        this.displayValue = text;
      }
    },
    formattedInput(value) {
      const text = String(value ?? "");
      const explicitInternational = /^\s*(?:\+|00)/.test(text);
      const input = explicitInternational ? text.trim().replace(/^00/, "+") : text;
      const formatter = new AsYouType(explicitInternational ? undefined : this.selectedCountry || undefined);
      const formatted = formatter.input(input);

      if (explicitInternational) {
        const detected = formatter.getCountry();
        if (detected) {
          this.selectedCountry = detected;
          this.infoKey = "phone_field.detected_country";
        }
      } else {
        this.infoKey = "";
      }
      return formatted;
    },
    onInput(value) {
      this.dirty = true;
      this.hasBlurred = false;
      this.candidates = [];
      this.rawValue = String(value ?? "");
      if (/^\s*(?:\+|00)/.test(this.rawValue)) this.countryLocked = true;
      this.displayValue = this.formattedInput(value);
      this.result = this.parseInput(this.displayValue);
      this.emitResult(this.result, this.rawValue);
    },
    onCountryChange(country) {
      this.selectedCountry = resolvePhoneCountry(country);
      this.countryLocked = true;
      this.dirty = true;
      this.hasBlurred = false;
      this.infoKey = "";
      this.candidates = [];
      this.displayValue = this.formattedInput(this.rawValue);
      this.result = this.parseInput(this.displayValue);
      const value = this.emitResult(this.result, this.rawValue);
      this.$emit("change", value);
    },
    onBlur() {
      // SC-04: неизменённый номер «проверьте номер» сохраняется как есть — уход из поля
      // (например, Tab) не должен тихо подменять его разобранным значением.
      if (this.needsCheck && !this.dirty) {
        this.$emit("blur");
        return;
      }
      this.hasBlurred = true;
      const parsed = this.parseInput(this.rawValue);
      this.result = parsed;
      this.candidates = parsed.status === "ambiguous" ? parsed.candidates : [];

      // Номер, прочитанный с кодом страны («+…» или код без «+»), после ухода из поля
      // показывается местным: код страны уже на кнопке слева.
      if (parsed.status === "ok" && parsed.via !== "local") {
        if (parsed.country) this.selectedCountry = parsed.country;
        this.displayValue = this.nationalDisplay(parsed.e164);
        if (parsed.via === "international_no_plus") this.infoKey = "phone_field.country_code_present";
      }
      const value = this.emitResult(parsed, this.rawValue);
      this.$emit("change", value);
      this.$emit("blur");
      this.$nextTick(() => this.$refs.phone?.validate());
    },
    selectCandidate(candidate) {
      this.selectedCountry = candidate.country || this.selectedCountry;
      this.countryLocked = true;
      this.displayValue = this.nationalDisplay(candidate.e164);
      this.rawValue = candidate.e164;
      this.result = {
        status: "ok",
        e164: candidate.e164,
        country: candidate.country,
        via: "international_prefix",
      };
      this.candidates = [];
      this.hasBlurred = true;
      this.infoKey = "";
      this.emitValue(candidate.e164);
      this.$emit("change", candidate.e164);
      this.$nextTick(() => this.$refs.phone?.validate());
    },
    emitResult(result, fallback) {
      const value = result.status === "ok" ? result.e164 : result.status === "empty" ? "" : fallback;
      this.emitValue(value);
      return value;
    },
    emitValue(value) {
      this.lastEmitted = value;
      this.$emit("input", value);
    },
    validate() {
      this.hasBlurred = true;
      this.$refs.phone?.validate();
      return this.isValid;
    },
  },
};
</script>

<style scoped>
.phone-field__calling-code--hidden {
  visibility: hidden;
}

.phone-field__number {
  width: 100%;
  min-width: 0;
}

.phone-field__country-button {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: 4px;
  height: 24px;
  margin-right: 8px;
  padding: 0 8px 0 0;
  border: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.24);
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: 1;
  white-space: nowrap;
}

.phone-field__country-button:disabled {
  cursor: default;
}

.phone-field__country-button:focus-visible {
  outline: 2px solid var(--v-primary-base, #1976d2);
  outline-offset: 2px;
}

.theme--dark .phone-field__country-button {
  border-right-color: rgba(255, 255, 255, 0.3);
}

.phone-field__country-chevron {
  font-size: 10px;
}

.phone-field__country-menu {
  min-width: 280px;
}

.phone-field__country-search {
  position: sticky;
  z-index: 1;
  top: 0;
  padding: 8px;
  background: inherit;
}

.phone-field__candidate-message {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.phone-field__candidate {
  min-width: 0 !important;
  padding: 0 2px !important;
  text-transform: none;
}
</style>
